import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAuth } from '@/presentation/hooks/use-auth.js';
import { formatDate } from '@/shared/utils/format-date.js';

interface Props {
    controller: AuthController;
}

export function MyProfilePage({ controller }: Props) {
    const { translate, useAuthStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const { changePassword, isLoading } = useAuth(controller);
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');

    const criteria = {
        length: newPassword.length >= 8,
        upper: /[A-Z]/.test(newPassword),
        lower: /[a-z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        match: newPassword.length > 0 && newPassword === confirmPassword
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!currentPassword) {
            setError(translate('profile.currentPasswordRequired'));
            return;
        }
        if (!criteria.length || !criteria.upper || !criteria.lower || !criteria.number) {
            setError(translate('auth.passwordNotMet'));
            return;
        }
        if (!criteria.match) {
            setError(translate('profile.passwordsDoNotMatch'));
            return;
        }
        const success = await changePassword(currentPassword, newPassword, confirmPassword);
        if (success) {
            setToast(translate('profile.passwordSaved'));
            setTimeout(() => setToast(''), 4000);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordSection(false);
        } else {
            setError(translate('errors.generic'));
        }
    };

    if (!user) {
        return null;
    }
    const initials = user.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((n: string) => n[0])
        .join('')
        .toUpperCase();

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            {toast && (
                <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, bgcolor: 'success.main', color: 'white', px: 2, py: 1.5, borderRadius: 2, boxShadow: 3 }}>
                    {toast}
                </Box>
            )}
            <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                {translate('profile.title')}
            </Typography>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {user.avatarUrl ? (
                                <img src={user.avatarUrl} alt={user.name} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                                <Typography sx={{ color: 'white', fontWeight: 600 }}>{initials}</Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {user.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user.email}
                            </Typography>
                            <Chip label={translate(`profiles.${user.profile}`)} size="small" sx={{ mt: 0.5 }} />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={4}>
                        <Box>
                            <Typography variant="caption" color="text.disabled">
                                {translate('profile.nameLabel')}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {user.name}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.disabled">
                                {translate('auth.email')}
                            </Typography>
                            <Typography variant="body2">{user.email}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.disabled">
                                {translate('profile.phone')}
                            </Typography>
                            <Typography variant="body2">{user.phone || '—'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.disabled">
                                {translate('profile.lastLogin')}
                            </Typography>
                            <Typography variant="body2">{formatDate(user.lastLoginAt)}</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                        {translate('profile.myAssignments')}
                    </Typography>
                    {user.assignments.length === 0 ? (
                        <Typography variant="body2" color="text.disabled">
                            {translate('profile.noAssignments')}
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('users.client')}</TableCell>
                                        <TableCell>{translate('nav.projects')}</TableCell>
                                        <TableCell>{translate('users.profileLabel')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {user.assignments.map((a: (typeof user.assignments)[number]) => (
                                        <TableRow key={a.id}>
                                            <TableCell>{a.clientName}</TableCell>
                                            <TableCell>{a.projectName ?? translate('profile.allProjectsOption')}</TableCell>
                                            <TableCell>
                                                <Chip label={a.profile} size="small" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => setShowPasswordSection(!showPasswordSection)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            {translate('profile.security')}
                        </Typography>
                        <IconButton size="small">
                            <ChevronDown size={18} style={{ transform: showPasswordSection ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </IconButton>
                    </Stack>
                    <Collapse in={showPasswordSection}>
                        <Box
                            component="form"
                            onSubmit={handleChangePassword}
                            sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            <Typography variant="body2" fontWeight={500}>
                                {translate('profile.changePasswordSection')}
                            </Typography>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label={translate('profile.currentPassword')}
                                    type={showCurrent ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <IconButton onClick={() => setShowCurrent(!showCurrent)} sx={{ position: 'absolute', right: 4, top: 4 }} size="small">
                                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                                </IconButton>
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label={translate('profile.newPasswordLabel')}
                                    type={showNew ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <IconButton onClick={() => setShowNew(!showNew)} sx={{ position: 'absolute', right: 4, top: 4 }} size="small">
                                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                                </IconButton>
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    label={translate('profile.confirmNewPasswordLabel')}
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    size="small"
                                    fullWidth
                                    error={!!confirmPassword && confirmPassword !== newPassword}
                                />
                                <IconButton onClick={() => setShowConfirm(!showConfirm)} sx={{ position: 'absolute', right: 4, top: 4 }} size="small">
                                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                </IconButton>
                            </Box>
                            {error && (
                                <Typography color="error" variant="body2">
                                    {error}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    onClick={() => {
                                        setShowPasswordSection(false);
                                        setCurrentPassword('');
                                        setNewPassword('');
                                        setConfirmPassword('');
                                        setError('');
                                    }}
                                >
                                    {translate('common.cancel')}
                                </Button>
                                <Button type="submit" variant="contained" disabled={isLoading} sx={{ color: 'white' }}>
                                    {isLoading ? translate('profile.passwordSaving') : translate('profile.saveNewPassword')}
                                </Button>
                            </Stack>
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        </Box>
    );
}
